import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { defineStore } from 'pinia';

import { env } from '@/env';
import { useAuthStore } from '@/stores/auth';
import { useFullstoryStore } from '@/stores/fullstory';
import { useTrackingStore } from '@/stores/tracking';
import { logger } from '@/utils/logger';

function registerTrackingSessionId() {
  const ddRumSessionId =
    datadogLogs?.getInternalContext()?.session_id ??
    datadogRum?.getInternalContext()?.session_id ??
    null;
  if (!ddRumSessionId) {
    logger.warn(`Datadog Session ID Missing`);
  }
  useTrackingStore().setSessionId(ddRumSessionId);
  return ddRumSessionId;
}

export const useDatadogStore = defineStore('datadog', () => {
  const authStore = useAuthStore();
  const fullstoryStore = useFullstoryStore();

  const enabled = env.datadogTraceEnabled && !window.Cypress && !env.isLocalDev;
  const clientToken = env.datadogClientToken;
  const applicationId = env.datadogApplicationId;
  const mode = env.mode;
  const version = env.revision;

  // Errors we never want to ship to RUM. Two categories, same outcome:
  //   - Transient network state (user navigated mid-request, lost connection) —
  //     not a real bug.
  //   - Benign browser warnings surfaced via window.onerror. ResizeObserver
  //     loop notices are spec-defined behavior (W3C resize-observer §3.4) that
  //     Chromium reports as Errors; the browser handles them on the next frame.
  const IGNORED_ERROR_PATTERNS = [
    /^(AxiosError: )?Network Error$/,
    /^(AxiosError: )?Request aborted$/,
    /^(Uncaught "?)?ResizeObserver loop completed with undelivered notifications\.?"?$/,
    /^(Uncaught "?)?ResizeObserver loop limit exceeded"?$/,
  ];

  // Request timeouts: client-perceived slowness, not JS bugs — log a breadcrumb, drop from RUM.
  const TIMEOUT_ERROR_PATTERNS = [
    /^(AxiosError: )?timeout exceeded$/,
    /^(AxiosError: )?timeout of \d+ms exceeded$/,
  ];

  function isIgnoredError(event) {
    if (event.type !== 'error') return false;
    const message = event.error?.message ?? '';
    return IGNORED_ERROR_PATTERNS.some((pattern) => pattern.test(message));
  }

  function isTimeoutError(event) {
    if (event.type !== 'error') return false;
    const message = event.error?.message ?? '';
    return TIMEOUT_ERROR_PATTERNS.some((pattern) => pattern.test(message));
  }

  const SURVICATE_FETCH_MESSAGE = /Failed to fetch/i;
  const SURVICATE_SOURCE = /survicate|widget_core/i;

  /**
   * The Survicate survey widget is a remote, auto-updating script (widget_core-*.js served from
   * survey.survicate.com) that fires its own fetch() calls. Those fail on transient network state —
   * the user navigated mid-request, lost connection, or Survicate's CDN was briefly unreachable —
   * surfacing as an uncaught "Failed to fetch". It's a third-party script we neither control nor pin,
   * and not a dash-web bug. Scope the match to Survicate's stack so we don't mask our own fetch
   * failures; the caller logs it (→ Datadog Logs, not RUM) and drops it from RUM error tracking.
   */
  function isSurvicateFetchError(event) {
    if (event.type !== 'error') return false;
    const message = event.error?.message ?? '';
    const stack = event.error?.stack ?? '';
    return SURVICATE_FETCH_MESSAGE.test(message) && SURVICATE_SOURCE.test(stack);
  }

  function beforeSend(event) {
    if (isIgnoredError(event)) return false;
    if (isTimeoutError(event)) {
      logger.warn('Request timed out', {
        errorMessage: event.error?.message,
        resourceUrl: event.error?.resource?.url,
      });
      return false;
    }
    if (isSurvicateFetchError(event)) {
      logger.error('Survicate survey widget fetch failed', {
        errorMessage: event.error?.message,
      });
      return false;
    }
    registerTrackingSessionId();
    if (event.type === 'error' && fullstoryStore.isFullStoryEnabled) {
      event.context = {
        ...event.context,
        fullStory: {
          fullStoryUrl: fullstoryStore.getFullStoryUrl(),
        },
      };

      try {
        fullstoryStore.sendFullstoryEvent('RUM Error', { ...event });
      } catch {
        logger.debug('Unable to report RUM error details to FullStory');
      }
    }
    return true;
  }

  // Logs-SDK counterpart to beforeSend — drop the same transient noise console forwarding leaks in.
  function logsBeforeSend(log) {
    const message = log?.message ?? '';
    return ![...IGNORED_ERROR_PATTERNS, ...TIMEOUT_ERROR_PATTERNS].some((pattern) =>
      pattern.test(message),
    );
  }

  function init() {
    if (enabled) {
      const commonConfig = {
        clientToken,
        service: 'dash-web',
        env: mode,
        version,
        site: 'datadoghq.com',
        proxy: (options) => `https://dd.dashsocial.com/${options.path}?${options.parameters}`,
      };
      // running vue 3 migration with datadog logs causes issues
      if (env.isProduction) {
        datadogLogs.init({
          ...commonConfig,
          sessionSampleRate: 100,
          forwardErrorsToLogs: false,
          forwardConsoleLogs: ['warn', 'error'],
          forwardReports: ['csp_violation', 'intervention'],
          beforeSend: logsBeforeSend,
        });
      }
      datadogRum.init({
        ...commonConfig,
        applicationId,
        allowedTracingUrls: [
          (url) => {
            // ChiliPiper calls include our full domain in the URL, causing x-datadog-* headers
            // being added to requests to ChiliPiper, who disallow those headers
            if (url.includes('chilipiper') || url.includes('hubspot')) {
              return false;
            }
            return [
              /https:\/\/(?!cdn|images|mp).*\.dhdev\.co/,
              /https:\/\/(?!cdn|images|mp).*\.dashhudson\.com/,
              /https:\/\/(?!cdn|images|mp).*\.dashsocial\.com/,
            ].some((expr) => expr.test(url));
          },
        ],
        sessionSampleRate: 100,
        sessionReplaySampleRate: 0,
        traceSampleRate: 100,
        trackResources: true,
        beforeSend,
      });
      registerTrackingSessionId();
    }
  }

  function setupUser() {
    if (enabled) {
      const identity = authStore.identity;
      const currentBrand = authStore.currentBrand;

      if (identity) {
        const user = {
          id: identity.id,
          name: `${identity.first_name} ${identity.last_name}`,
          email: identity.email,
        };
        datadogLogs.setUser(user);
        datadogRum.setUser(user);
      } else {
        datadogLogs.clearUser();
        datadogRum.clearUser();
      }

      if (currentBrand) {
        const brand = {
          id: currentBrand.id,
          name: currentBrand.name,
          label: currentBrand.label,
        };
        datadogLogs.setGlobalContextProperty('brand', brand);
        datadogRum.setGlobalContextProperty('brand', brand);
      } else {
        datadogLogs.removeGlobalContextProperty('brand');
        datadogRum.removeGlobalContextProperty('brand');
      }
    }
  }

  function addError(...args) {
    if (enabled) {
      datadogRum.addError(...args);
    }
  }

  function addAction(...args) {
    if (enabled) {
      datadogRum.addAction(...args);
    }
  }

  return {
    init,
    setupUser,
    beforeSend,
    logsBeforeSend,
    addError,
    addAction,
  };
});
